import { USAGE_METER_CATALOG } from 'features/usage-config/constants/usage-meters';
import {
  IKlaxUsageMetadata,
  ILimitWindow,
  IMeterSetting,
  IModelCategory,
  IModelRestriction,
  IUsageEnforcementConfig,
  IUsageEnforcementPayload,
  SettlementStrategy,
} from 'features/usage-config/types/usage-enforcement.model';

import { renameWindowShare, withoutWindowShare } from './limit-windows';

export const OVERRIDABLE_FIELDS = [
  'is_blocked',
  'enforcement_enabled',
  'meter_keys',
  'settlement',
  'model_restriction',
] as const;

export type OverridableField = (typeof OVERRIDABLE_FIELDS)[number];

export interface MeterDraft {
  enabled: boolean;
}

export interface UsageDraft {
  isBlocked: boolean;
  enforcementEnabled: boolean;
  meters: Record<string, MeterDraft>;
  settlement: SettlementStrategy;
  limitWindows: ILimitWindow[];
  modelRestrictionsEnabled: boolean;
  modelCategories: IModelCategory[];
  modelRestriction: IModelRestriction;
}

export const emptyConfig = (): IUsageEnforcementConfig => ({
  is_blocked: false,
  enforcement_enabled: true,
  meter_keys: USAGE_METER_CATALOG.map((meter) => ({ key: meter.meterKey, enabled: true })),
  enabled_meter_keys: USAGE_METER_CATALOG.map((meter) => meter.meterKey),
  settlement: 'org',
  gate_meta_data: {},
  metadata: {},
  model_restriction: { enabled: false, models: [] },
  capabilities: { allowed: null, disallowed: null },
  organization_id: '',
  scope: 'defaults',
  is_saved: false,
  overridden_fields: [],
});

export const draftFromConfig = (config: IUsageEnforcementConfig): UsageDraft => {
  const klax: IKlaxUsageMetadata = config.metadata ?? {};
  return {
    isBlocked: config.is_blocked,
    enforcementEnabled: config.enforcement_enabled,
    meters: Object.fromEntries(
      USAGE_METER_CATALOG.map((meter) => {
        const stored = config.meter_keys.find((row) => row.key === meter.meterKey);
        return [meter.meterKey, { enabled: stored?.enabled ?? true }];
      })
    ),
    settlement: config.settlement ?? 'org',
    limitWindows: (klax.limit_windows ?? []).map((limitWindow) => ({ ...limitWindow })),
    modelRestrictionsEnabled: klax.model_restrictions_enabled ?? false,
    modelCategories: (klax.model_categories ?? []).map((category) => ({
      ...category,
      models: [...category.models],
      window_share: { ...(category.window_share ?? {}) },
    })),
    modelRestriction: {
      enabled: config.model_restriction?.enabled ?? false,
      models: [...(config.model_restriction?.models ?? [])],
    },
  };
};

/**
 * Replacing the window list. A key that disappeared is removed from every
 * category share, because those shares point at the key.
 */
export const withLimitWindows = (draft: UsageDraft, windows: ILimitWindow[]): UsageDraft => {
  // Same length is an edit. A key that moved in place keeps its category shares.
  // A different length is a window added or removed, and a removed key drops its shares.
  if (draft.limitWindows.length === windows.length) {
    const modelCategories = draft.limitWindows.reduce(
      (categories, limitWindow, index) =>
        renameWindowShare(categories, limitWindow.key, windows[index].key),
      draft.modelCategories
    );
    return { ...draft, limitWindows: windows, modelCategories };
  }
  const removed = draft.limitWindows
    .map((limitWindow) => limitWindow.key)
    .filter((key) => windows.every((next) => next.key !== key));
  if (removed.length === 0) return { ...draft, limitWindows: windows };
  return {
    ...draft,
    limitWindows: windows,
    modelCategories: removed.reduce(withoutWindowShare, draft.modelCategories),
  };
};

export const metersFromDraft = (draft: UsageDraft): IMeterSetting[] =>
  USAGE_METER_CATALOG.map((meter) => ({
    key: meter.meterKey,
    enabled: draft.meters[meter.meterKey]?.enabled ?? true,
  }));

export const sameFields = (left: Iterable<string>, right: Iterable<string>): boolean => {
  const a = [...left].sort();
  const b = [...right].sort();
  return a.length === b.length && a.every((value, index) => value === b[index]);
};

export const restoreField = (
  draft: UsageDraft,
  source: UsageDraft,
  field: OverridableField
): UsageDraft => {
  switch (field) {
    case 'is_blocked':
      return { ...draft, isBlocked: source.isBlocked };
    case 'enforcement_enabled':
      return { ...draft, enforcementEnabled: source.enforcementEnabled };
    case 'meter_keys':
      return { ...draft, meters: source.meters };
    case 'settlement':
      return { ...draft, settlement: source.settlement };
    case 'model_restriction':
      return { ...draft, modelRestriction: source.modelRestriction };
    default:
      return draft;
  }
};

export const buildTenantPayload = (draft: UsageDraft): IUsageEnforcementPayload => ({
  is_blocked: draft.isBlocked,
  enforcement_enabled: draft.enforcementEnabled,
  meter_keys: metersFromDraft(draft),
  settlement: draft.settlement,
  model_restriction: draft.modelRestriction,
});

/**
 * Klax's own rules, saved into the tenant's metadata through the upsert. Windows follow
 * the plan's billing period, so the timezone an older screen stored is removed.
 */
export const buildTenantMetadata = (draft: UsageDraft): IKlaxUsageMetadata => ({
  timezone: null,
  limit_windows: draft.limitWindows,
  model_restrictions_enabled: draft.modelRestrictionsEnabled,
  model_categories: draft.modelCategories,
});

/**
 * Null gives a field back to the tenant, and every overridable field travels that way.
 * Meters merge per key, so an override sends only the keys that differ — an empty list
 * sets nothing and the rest keep following the tenant.
 */
export const buildOrganizationPayload = (
  draft: UsageDraft,
  overrides: ReadonlySet<string>,
  tenant?: UsageDraft
): IUsageEnforcementPayload => ({
  is_blocked: overrides.has('is_blocked') ? draft.isBlocked : null,
  enforcement_enabled: overrides.has('enforcement_enabled') ? draft.enforcementEnabled : null,
  meter_keys: overrides.has('meter_keys') ? changedMeters(draft, tenant) : [],
  settlement: overrides.has('settlement') ? draft.settlement : null,
  model_restriction: overrides.has('model_restriction') ? draft.modelRestriction : null,
});

const changedMeters = (draft: UsageDraft, tenant?: UsageDraft): IMeterSetting[] => {
  const meters = metersFromDraft(draft);
  if (!tenant) return meters;
  return meters.filter((meter) => {
    const base = tenant.meters[meter.key];
    return !base || base.enabled !== meter.enabled;
  });
};
